import {
  circle,
  ellipse,
  fill,
  flat,
  INK,
  line,
  P,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

/* Geometry helpers for this set: the icon model has no transforms, so the math happens here.
 * Angles are degrees, clockwise like the screen: 0 = right, 90 = down, -90 = up. */

type Pt = readonly [number, number];

const RAD = Math.PI / 180;
const r2 = (v: number): number => Math.round(v * 100) / 100;
const pt = (p: Pt): string => `${r2(p[0])} ${r2(p[1])}`;
const add = (p: Pt, v: Pt, k = 1): Pt => [p[0] + v[0] * k, p[1] + v[1] * k];
/** A unit vector toward `deg`. */
const dir = (deg: number): Pt => [Math.cos(deg * RAD), Math.sin(deg * RAD)];

/** Flat x, y pairs as points. */
function xy(n: readonly number[]): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i + 1 < n.length; i += 2) out.push([n[i] as number, n[i + 1] as number]);
  return out;
}

/** `p` turned `deg` degrees around `c`. */
function turn(p: Pt, deg: number, c: Pt = [16, 16]): Pt {
  const [cos, sin] = dir(deg);
  const x = p[0] - c[0];
  const y = p[1] - c[1];
  return [c[0] + x * cos - y * sin, c[1] + x * sin + y * cos];
}

/** The corner at `p` (coming from `a`, going to `b`) cut back `k` along both edges. */
function cut(a: Pt, p: Pt, b: Pt, k: number): [Pt, Pt] {
  const da = Math.hypot(a[0] - p[0], a[1] - p[1]);
  const db = Math.hypot(b[0] - p[0], b[1] - p[1]);
  const ka = Math.min(k, da / 2) / da;
  const kb = Math.min(k, db / 2) / db;
  return [add(p, [a[0] - p[0], a[1] - p[1]], ka), add(p, [b[0] - p[0], b[1] - p[1]], kb)];
}

/** A closed polygon with soft corners, each cut back `r` (one for all, or one per corner). */
function softPoly(pts: readonly Pt[], r: number | readonly number[]): string {
  let d = '';
  pts.forEach((p, i) => {
    const k = typeof r === 'number' ? r : (r[i % r.length] ?? 0);
    const [s, e] = cut(pts[(i + pts.length - 1) % pts.length] as Pt, p, pts[(i + 1) % pts.length] as Pt, k);
    d += `${i ? 'L' : 'M'}${pt(s)}Q${pt(p)} ${pt(e)}`;
  });
  return `${d}Z`;
}

/** Path commands from the current point pts[0] on through the rest, softening the corners between. */
function softRun(pts: readonly Pt[], r: readonly number[]): string {
  let d = '';
  for (let i = 1; i < pts.length - 1; i++) {
    const [s, e] = cut(pts[i - 1] as Pt, pts[i] as Pt, pts[i + 1] as Pt, r[i - 1] ?? 0);
    d += `L${pt(s)}Q${pt(pts[i] as Pt)} ${pt(e)}`;
  }
  return `${d}L${pt(pts[pts.length - 1] as Pt)}`;
}

/** A fat bar with round ends from a to b, `r` = half its thickness. */
function bar(a: Pt, b: Pt, r: number): string {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const n: Pt = [((a[1] - b[1]) / len) * r, ((b[0] - a[0]) / len) * r];
  const k = r2(r);
  return (
    `M${pt(add(a, n))}L${pt(add(b, n))}A${k} ${k} 0 0 0 ${pt(add(b, n, -1))}` +
    `L${pt(add(a, n, -1))}A${k} ${k} 0 0 0 ${pt(add(a, n))}Z`
  );
}

/** A step for band(): a number goes straight that far, [degrees, radius] curves (+ = clockwise). */
type Move = number | readonly [number, number];

/**
 * A fat stroke as one outlined shape, drawn like a turtle: from `from`, heading `heading`,
 * through the moves, `thick` wide, with a round tail and either a round end or an
 * arrowhead [length, half-width].
 */
function band(
  from: Pt,
  heading: number,
  moves: readonly Move[],
  thick: number,
  end: 'round' | readonly [number, number] = 'round',
): string {
  const h = thick / 2;
  // a point beside the path: k > 0 is to the right of the way it's going
  const side = (p: Pt, a: number, k: number): Pt => add(p, dir(a + 90), k);
  let p = from;
  let a = heading;
  let d = `M${pt(side(p, a, -h))}`;
  let back = '';
  for (const m of moves) {
    const p0 = p;
    const a0 = a;
    if (typeof m === 'number') {
      p = add(p, dir(a), m);
      d += `L${pt(side(p, a, -h))}`;
      back = `L${pt(side(p0, a0, h))}${back}`;
    } else {
      const [deg, r] = m;
      const cw = deg > 0;
      const c = side(p, a, cw ? r : -r);
      a += deg;
      p = side(c, a, cw ? -r : r);
      const big = Math.abs(deg) > 180 ? 1 : 0;
      const rl = r2(cw ? r + h : r - h);
      const rr = r2(cw ? r - h : r + h);
      d += `A${rl} ${rl} 0 ${big} ${cw ? 1 : 0} ${pt(side(p, a, -h))}`;
      back = `A${rr} ${rr} 0 ${big} ${cw ? 0 : 1} ${pt(side(p0, a0, h))}${back}`;
    }
  }
  if (end === 'round') d += `A${r2(h)} ${r2(h)} 0 0 1 ${pt(side(p, a, h))}`;
  else {
    const [len, half] = end;
    const tip = [side(p, a, -h), side(p, a, -half), add(p, dir(a), len), side(p, a, half), side(p, a, h)];
    d += softRun(tip, [1, 1.4, 1]);
  }
  return `${d}${back}A${r2(h)} ${r2(h)} 0 0 1 ${pt(side(from, heading, -h))}Z`;
}

/** A water drop: round end on `c` (radius r), its point `len` from c toward `deg`. */
function teardrop(c: Pt, r: number, len: number, deg = -90): string {
  const q = (x: number, y: number): string => pt(turn([c[0] + x, c[1] + y], deg + 90, c));
  const k = r2(r);
  return (
    `M${q(0, -len)}C${q(r * 0.3, -len + r * 0.55)} ${q(r, -r * 0.8)} ${q(r, 0)}` +
    `A${k} ${k} 0 0 1 ${q(-r, 0)}C${q(-r, -r * 0.8)} ${q(-r * 0.3, -len + r * 0.55)} ${q(0, -len)}Z`
  );
}

/** A four-point sparkle reaching `r` out from (x, y); `k` sets how fat the arms are. */
function spark(x: number, y: number, r: number, k = 0.22): string {
  const s = r * k;
  const q = (dx: number, dy: number): string => pt([x + dx, y + dy]);
  return `M${q(0, -r)}Q${q(s, -s)} ${q(r, 0)}Q${q(s, s)} ${q(0, r)}Q${q(-s, s)} ${q(-r, 0)}Q${q(-s, -s)} ${q(0, -r)}Z`;
}

/** The corners of a star or burst around c: spikes at the outer radii, dips at the inner ones. */
function spikes(c: Pt, outer: readonly number[], inner: readonly number[], deg = -90): Pt[] {
  const out: Pt[] = [];
  outer.forEach((r, i) => {
    out.push(add(c, dir(deg + (360 / outer.length) * i), r));
    out.push(add(c, dir(deg + (360 / outer.length) * (i + 0.5)), inner[i % inner.length] ?? 0));
  });
  return out;
}

/** An arc along circle (c, r) from `from` to `to`, going round the side that passes `via`. */
function arcVia(c: Pt, r: number, from: Pt, to: Pt, via: Pt): string {
  const ang = (p: Pt): number => Math.atan2(p[1] - c[1], p[0] - c[0]);
  const norm = (v: number): number => ((v % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const span = norm(ang(to) - ang(from));
  const cw = norm(ang(via) - ang(from)) < span;
  const sweep = cw ? span : 2 * Math.PI - span;
  return `A${r2(r)} ${r2(r)} 0 ${sweep > Math.PI ? 1 : 0} ${cw ? 1 : 0} ${pt(to)}`;
}

/** Where two circles cross (they must). */
function hits(c1: Pt, r1: number, c2: Pt, rb: number): [Pt, Pt] {
  const dx = c2[0] - c1[0];
  const dy = c2[1] - c1[1];
  const d = Math.hypot(dx, dy);
  const a = (r1 * r1 - rb * rb + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
  const m = add(c1, [dx / d, dy / d], a);
  return [add(m, [-dy / d, dx / d], h), add(m, [dy / d, -dx / d], h)];
}

/** Circle (c1, r1) with circle (c2, rb) bitten out of it. */
function crescent(c1: Pt, r1: number, c2: Pt, rb: number): string {
  const [a, b] = hits(c1, r1, c2, rb);
  const len = Math.hypot(c1[0] - c2[0], c1[1] - c2[1]);
  const u: Pt = [(c1[0] - c2[0]) / len, (c1[1] - c2[1]) / len];
  return `M${pt(a)}${arcVia(c1, r1, a, b, add(c1, u, r1))}${arcVia(c2, rb, b, a, add(c2, u, rb))}Z`;
}

/** A puffy cloud: the outline of round bumps [x, y, r], listed in order around the edge. */
function cloud(bumps: readonly (readonly [number, number, number])[]): string {
  const n = bumps.length;
  const mid: Pt = [bumps.reduce((s, b) => s + b[0], 0) / n, bumps.reduce((s, b) => s + b[1], 0) / n];
  const far = (p: Pt): number => Math.hypot(p[0] - mid[0], p[1] - mid[1]);
  // where each bump meets the next one, on the outside
  const joins = bumps.map((b, i) => {
    const c = bumps[(i + 1) % n] as readonly [number, number, number];
    const [p, q] = hits([b[0], b[1]], b[2], [c[0], c[1]], c[2]);
    return far(p) > far(q) ? p : q;
  });
  let d = `M${pt(joins[n - 1] as Pt)}`;
  bumps.forEach(([x, y, r], i) => {
    const len = Math.hypot(x - mid[0], y - mid[1]) || 1;
    const out = add([x, y], [(x - mid[0]) / len, (y - mid[1]) / len], r);
    d += arcVia([x, y], r, joins[(i + n - 1) % n] as Pt, joins[i] as Pt, out);
  });
  return `${d}Z`;
}

/** An ellipse turned `deg`; `back` runs it the other way round, to cut a hole in a shape. */
function oval(c: Pt, rx: number, ry: number, deg = 0, back = false): string {
  const a = add(c, dir(deg), rx);
  const b = add(c, dir(deg), -rx);
  const s = `A${r2(rx)} ${r2(ry)} ${r2(deg)} 1 ${back ? 0 : 1} `;
  return `M${pt(a)}${s}${pt(b)}${s}${pt(a)}Z`;
}

/** A leaf (a pointed lens) from its base a to its tip b, about `w` wide each side. */
function leaf(a: Pt, b: Pt, w: number): string {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const u: Pt = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
  const n: Pt = [-u[1], u[0]];
  const at = (t: number, k: number): string => pt(add(add(a, u, len * t), n, (k * w) / 0.62));
  return `M${pt(a)}C${at(0.25, 0.9)} ${at(0.7, 0.75)} ${pt(b)}C${at(0.7, -0.75)} ${at(0.25, -0.9)} ${pt(a)}Z`;
}

/** A stroke that reads like an outlined shape: an ink line under a colored one `w` wide. */
const inked = (d: string, w: number, color: string, o = 1.6): Shape[] => [
  line(d, w + 2 * o, INK),
  line(d, w, color),
];

/** An arrowhead's corners: the tip, pointing toward `deg`, `len` long and `half` wide each side. */
function head(tip: Pt, deg: number, len: number, half: number): Pt[] {
  const base = add(tip, dir(deg), -len);
  return [tip, add(base, dir(deg + 90), half), add(base, dir(deg - 90), half)];
}

/** The exemplar heart's outline (centered on 16, 16), to scale and turn. */
const HEART = xy([
  16, 27, 10, 22.4, 4, 18.2, 4, 11.6, 4, 7.6, 7, 5, 10.3, 5, 12.8, 5, 14.8, 6.4, 16, 8.6, 17.2, 6.4, 19.2, 5,
  21.7, 5, 25, 5, 28, 7.6, 28, 11.6, 28, 18.2, 22, 22.4, 16, 27,
]);

/** The heart centered on (x, y), scaled by s and turned `deg`, with its shine (if it's big enough for one). */
function heartAt(x: number, y: number, s: number, deg = 0, color: string = P.red, shiny = true): Shape[] {
  const q = HEART.map((p) => pt(turn([x + (p[0] - 16) * s, y + (p[1] - 16) * s], deg, [x, y])));
  let d = `M${q[0]}`;
  for (let i = 1; i + 2 < q.length; i += 3) d += `C${q[i]} ${q[i + 1]} ${q[i + 2]}`;
  const [sx, sy] = turn([x - 5.6 * s, y - 5.6 * s], deg, [x, y]);
  return [fill(color, `${d}Z`), ...(shiny ? [shine(ellipse(sx, sy, 2.6 * s, 1.8 * s), 0.55)] : [])];
}

/** The bold objective arrow, pointing up, centered on the box. The other three are it, turned. */
const ARROW = xy([16, 2.5, 28, 14.5, 21, 14.5, 21, 28.8, 11, 28.8, 11, 14.5, 4, 14.5]);
const arrow = (deg: number): IconDef => [
  fill(
    P.gold,
    softPoly(
      ARROW.map((p) => turn(p, deg)),
      [2, 1.6, 0.5, 1.4, 1.4, 0.5, 1.6],
    ),
  ),
];

/** The box shared by check, empty and play (cross is drawn to the same size). */
const BOX = rect(4, 4, 24, 24, 5.5);

/** A ring with a slash from top-left to bottom-right: a disc with two half-holes cut out. */
function noSign(c: Pt, ro: number, ri: number, half: number): string {
  const along = dir(45);
  const across = dir(-45);
  const s0 = Math.sqrt(ri * ri - half * half);
  let d = `M${pt(add(c, [-ro, 0]))}A${ro} ${ro} 0 1 1 ${pt(add(c, [ro, 0]))}A${ro} ${ro} 0 1 1 ${pt(add(c, [-ro, 0]))}Z`;
  for (const k of [1, -1]) {
    const m = add(c, across, half * k);
    const a = add(m, along, -s0 * k);
    const b = add(m, along, s0 * k);
    d += `M${pt(a)}L${pt(b)}${arcVia(c, ri, b, a, add(c, across, ri * k))}Z`;
  }
  return d;
}

/** A cherry blossom: five notched petals around c, scaled by s. */
function blossomAt(c: Pt, s = 1): string {
  // the right half of the petal pointing up, from its notch out and back in to the dip
  const right = xy([
    0, -11.2, 0.6, -12.6, 1.4, -13.3, 2.8, -13.3, 4.6, -13.3, 6.1, -12.3, 5.9, -10.4, 5.7, -8, 3.6, -5, 2.23,
    -3.07,
  ]);
  const petal = [...right.map(([x, y]): Pt => [-x, y]).reverse(), ...right.slice(1)];
  let d = '';
  for (let i = 0; i < 5; i++) {
    const q = petal.map((p) => pt(turn(add(c, p, s), i * 72, c)));
    d += `${i ? 'L' : 'M'}${q[0]}`;
    for (let j = 1; j + 2 < q.length; j += 3) d += `C${q[j]} ${q[j + 1]} ${q[j + 2]}`;
  }
  return `${d}Z`;
}

/** A 'Z' with its top-left at (x, y), s wide, strokes t thick. */
function zee(x: number, y: number, s: number, t: number): string {
  const h = s * 1.05;
  const a = t * 1.5;
  const z: Pt[] = [
    [x, y],
    [x + s, y],
    [x + s, y + t],
    [x + a, y + h - t],
    [x + s, y + h - t],
    [x + s, y + h],
    [x, y + h],
    [x, y + h - t],
    [x + s - a, y + t],
    [x, y + t],
  ];
  return softPoly(z, [0.6, 0.6, 0.4, 0.3, 0.4, 0.6, 0.6, 0.4, 0.3, 0.4]);
}

export const symbols = {
  heart: [
    fill(
      P.red,
      'M16 27C10 22.4 4 18.2 4 11.6C4 7.6 7 5 10.3 5C12.8 5 14.8 6.4 16 8.6C17.2 6.4 19.2 5 21.7 5C25 5 28 7.6 28 11.6C28 18.2 22 22.4 16 27Z',
    ),
    shine(ellipse(10.4, 10.4, 2.6, 1.8), 0.55),
  ],
  fire: [
    fill(
      P.fire,
      'M16 29C9.5 29 6 24.6 6 19.6C6 14 10.5 11.4 11.2 6.6C14.4 8.6 15.6 11.4 15.4 14.2C17 12.6 17.6 9.4 17 5.6C22.6 8.6 26 14 26 19.8C26 25 22.4 29 16 29Z',
    ),
    flat(
      P.flame,
      'M16 27.6C12.6 27.6 11 25.4 11 23C11 20.2 13.4 18.8 14 16.4C15.6 17.6 16.4 19.2 16.2 20.6C17.2 19.8 17.8 18.4 17.6 16.8C20 18.4 21 20.6 21 23C21 25.6 19.2 27.6 16 27.6Z',
    ),
  ],
  /** Two pink hearts, one big, one small. */
  hearts: [...heartAt(12.64, 19.4, 0.72, -12, P.pink), ...heartAt(22.64, 10.5, 0.46, 14, P.pink, false)],
  /** A red heart run through by a gold arrow (The Lovers): the shaft and feathers go behind it. */
  'heart-arrow': [
    fill(P.gold, bar([7.4, 24.6], [23.4, 8.6], 1.1), { w: 1.2 }),
    fill(
      P.white,
      softPoly(
        xy([0, -6.6, 3, -3.8, 3, 0.6, 0, -2.2, -3, 0.6, -3, -3.8]).map((p) =>
          add(turn(p, 45, [0, 0]), [6.2, 25.8]),
        ),
        0.5,
      ),
      { w: 1.3 },
    ),
    ...heartAt(16, 16.6, 0.8),
    fill(P.gold, softPoly(head([28.2, 3.8], -45, 6.6, 3.6), [1, 0.8, 0.8])),
  ],
  /** A yellow sun with rays. */
  sun: [
    fill(
      P.amber,
      [0, 1, 2, 3, 4, 5, 6, 7]
        .map((i) =>
          softPoly(
            xy([13.3, 9, 16, 3, 18.7, 9]).map((p) => turn(p, i * 45)),
            [0.4, 1.1, 0.4],
          ),
        )
        .join(''),
    ),
    fill(P.yellow, circle(16, 16, 8.4)),
    shine(ellipse(13, 12.6, 2.4, 1.6), 0.55),
  ],
  /** A pale yellow crescent moon. */
  moon: [fill(P.lemon, crescent([16.27, 15.96], 12.4, [22.27, 10.96], 10.4))],
  /** A pale full moon with craters. */
  'full-moon': [
    fill(P.lemon, circle(16, 16, 12.6)),
    flat(
      P.gold,
      circle(11.2, 12.2, 3) + circle(20, 19.8, 3.8) + circle(20.8, 9.8, 1.9) + circle(10.6, 21, 2),
      0.5,
    ),
    shine(ellipse(9.6, 8.6, 1.8, 1.2), 0.5),
  ],
  /** A gray tornado: swirling bands narrowing down in an S, a bit of debris flying. */
  tornado: [
    fill(P.lightGray, band([12.33, 25.75], 12, [[-24, 9]], 3.6)),
    fill(P.lightGray, band([12.07, 21.35], 12, [[-24, 17]], 4.2)),
    fill(P.lightGray, band([11.5, 16.75], 12, [[-24, 26]], 4.6)),
    fill(P.lightGray, band([9.62, 11.95], 12, [[-24, 35]], 5)),
    fill(P.lightGray, band([6.23, 6.95], 12, [[-24, 47]], 5.4)),
    fill(P.wood, softPoly(xy([24.6, 22.35, 27.8, 21.55, 28.2, 23.15, 25, 23.95]), 0.3), { w: 1.2 }),
    fill(P.brown, softPoly(xy([4.6, 24.95, 7, 24.15, 7.6, 26.55, 5.2, 27.35]), 0.3), { w: 1.2 }),
  ],
  /** A blue water wave curling over: a lighter face under the curl, white foam on the crest. */
  wave: [
    fill(
      P.blue,
      'M3 28V18.6C3 10 9.6 4 17 4C23.4 4 28.6 8.2 28.6 13.2C28.6 16.2 26.4 18 24 18C22 18 20.6 16.6 20.6 15C18.2 16.2 17.6 20.2 20.6 22.2C23 23.8 26.4 23.4 29 23V28Z',
    ),
    fill(
      P.water,
      'M20.6 15C18.2 16.2 17.6 20.2 20.6 22.2C23 23.8 26.4 23.4 29 23V28H15.6C13 25.2 14 18.6 20.6 15Z',
    ),
    flat(
      P.white,
      'M6.6 14C8.6 9 13 6.2 17.4 6.2C22.6 6.2 26.2 9.2 26.6 13C25 10 21.8 8.2 18 8.4C13.4 8.6 9.8 11 6.6 14Z',
    ),
    line('M23.4 14.6C22.8 12.6 24.2 11.2 25.8 11.8', 1.4, P.white),
  ],
  /** A single blue water drop. */
  drop: [fill(P.water, teardrop([16, 20.2], 8.2, 16.6)), shine(ellipse(12.4, 21.6, 1.6, 2.8), 0.6)],
  /** Three blue sweat drops flying off. */
  sweat: [
    fill(P.water, teardrop([9, 8.6], 4.4, 9.2, 115)),
    fill(P.water, teardrop([20.6, 12.8], 5.8, 12, 140)),
    fill(P.water, teardrop([24, 23.8], 3.8, 8, 165)),
    shine(ellipse(22, 11, 1.6, 1.2), 0.6),
  ],
  /** A white puff of air with speed lines. */
  dash: [
    fill(P.white, bar([5.9, 13.2], [13.3, 13.2], 1.6)),
    fill(P.white, bar([5.1, 18.6], [12.7, 18.6], 1.6)),
    fill(
      P.white,
      cloud([
        [19.5, 11.6, 4.8],
        [24.7, 15.5, 3.7],
        [23, 21, 3.7],
        [16.7, 21.2, 4.1],
        [13.9, 15.7, 3.9],
      ]),
    ),
  ],
  /** Three yellow four-point sparkles. */
  sparkles: [
    fill(P.yellow, spark(12.8, 18.6, 9.6)),
    fill(P.yellow, spark(23.4, 9.2, 5.4)),
    fill(P.yellow, spark(24.6, 23.8, 4)),
  ],
  /** A gold five-point star. */
  star: [
    fill(P.gold, softPoly(spikes([16, 17.3], [13.6, 13.6, 13.6, 13.6, 13.6], [6.4]), [1.4, 0.6])),
    shine(ellipse(12.6, 13.2, 2, 1.3), 0.55),
  ],
  /** A green seedling with two leaves, in a little mound of dirt. */
  sprout: [
    fill(P.mud, 'M8.6 27.4C9.6 23.6 22.4 23.6 23.4 27.4Z'),
    ...inked('M16 24.4C16 20.8 16.6 17.8 16 13.8', 2, P.green),
    fill(P.lime, leaf([15.6, 14.4], [4.6, 8.4], 3.6)),
    fill(P.lime, leaf([16.4, 13.4], [27.6, 6.2], 4)),
    line('M14 13.2L8.4 10M18.4 11.8L24.2 8.2', 1.2, P.green),
  ],
  /** A sprig of lavender: two green leaves, a spike of purple buds. */
  herb: [
    ...inked('M13.6 27.2C14 22.6 15.6 18.6 18.2 15', 1.6, P.green),
    fill(P.green, leaf([14.2, 24.4], [7.2, 18.4], 1.8)),
    fill(P.green, leaf([14.9, 21.6], [22.4, 18.6], 1.8)),
    fill(
      P.purple,
      (
        [
          [17.2, 17.6, -125],
          [21, 16.4, -40],
          [18.4, 13.6, -120],
          [21.8, 12.4, -45],
          [19.4, 9.8, -115],
          [22.4, 8.6, -50],
          [21.2, 5.6, -75],
        ] as const
      )
        .map(([x, y, a]) => oval([x, y], 2.7, 1.75, a))
        .join(''),
      { w: 1.2 },
    ),
  ],
  /** A pine tree. */
  pine: [
    fill(P.brown, rect(13.6, 23, 4.8, 5.6, 0.8)),
    fill(
      P.green,
      softPoly(
        xy([
          16, 2.6, 22.4, 10.6, 19.8, 10.6, 25.4, 17.6, 22.2, 17.6, 28, 24.6, 4, 24.6, 9.8, 17.6, 6.6, 17.6,
          12.2, 10.6, 9.6, 10.6,
        ]),
        [1.4, 1, 0.4, 1, 0.4, 1, 1, 0.4, 1, 0.4, 1],
      ),
    ),
  ],
  /** A pink cherry blossom. */
  blossom: [
    fill(P.pink, blossomAt([16, 16.06], 0.92)),
    flat(P.hotPink, circle(16, 16.06, 3.4)),
    flat(P.yellow, circle(16, 16.06, 1.6)),
  ],
  /** Three pale blue "Z"s, getting bigger (sleeping). */
  zzz: [
    fill(P.sky, zee(3.3, 21.8, 6.8, 2.7), { w: 1.3 }),
    fill(P.sky, zee(9.5, 12.6, 9.2, 3.4), { w: 1.4 }),
    fill(P.sky, zee(16.7, 3, 12, 4.2), { w: 1.5 }),
  ],
  /** A red-yellow collision burst. */
  boom: [
    fill(
      P.red,
      softPoly(
        spikes([15.7, 16.4], [13.4, 11.4, 12.4, 11.6, 13.2, 12, 13.4, 11.4, 13], [7.6, 8.2]),
        [1, 0.5],
      ),
    ),
    flat(
      P.orange,
      softPoly(spikes([15.7, 16.4], [9.2, 7.6, 8.6, 7.8, 9, 8, 9.2, 7.6, 8.8], [5, 5.4]), [0.8, 0.4]),
    ),
    flat(
      P.yellow,
      softPoly(spikes([15.7, 16.4], [5.2, 4.4, 5, 4.4, 5, 4.6, 5.2, 4.4, 5], [2.8, 3]), [0.6, 0.3]),
    ),
  ],
  /** A yellow lightning bolt (energy). */
  bolt: [
    fill(
      P.yellow,
      softPoly(xy([21.6, 2.4, 6.4, 17.8, 14.2, 17.8, 10.6, 29.6, 25.6, 13.2, 17.8, 13.2]), [1.2, 1, 0.5]),
    ),
    shine(bar([18.4, 6.4], [12.4, 13.4], 0.9), 0.6),
  ],
  /** A white speech bubble (chat). */
  speech: [
    fill(P.white, 'M11.2 23.1A12.8 9.8 0 1 0 6.49 20.56Q7 25 5.2 27.8Q9.4 26.6 11.2 23.1Z'),
    flat(P.charcoal, circle(10.4, 14, 1.7) + circle(16, 14, 1.7) + circle(21.6, 14, 1.7)),
  ],
  /** A green box with a white check mark. */
  check: [fill(P.green, BOX), line(polyline(9.6, 16.4, 14, 20.8, 22.6, 11.2), 3.8, P.white)],
  /** A red X mark, as big as the check box. */
  cross: [
    fill(
      P.red,
      softPoly(
        xy([
          3.4, 14.3, -3.4, 14.3, -3.4, 3.4, -14.3, 3.4, -14.3, -3.4, -3.4, -3.4, -3.4, -14.3, 3.4, -14.3, 3.4,
          -3.4, 14.3, -3.4, 14.3, 3.4, 3.4, 3.4,
        ]).map((p) => turn(add(p, [16, 16]), 45)),
        [1.4, 1.4, 0.4],
      ),
    ),
  ],
  /** An empty white checkbox (the same box as check). */
  empty: [fill(P.white, BOX)],
  /** A red "no" sign: a ring with a slash. */
  no: [fill(P.red, noSign([16, 16], 12.8, 8.2, 2.5))],
  /** A yellow warning triangle with "!". */
  warning: [
    fill(P.yellow, softPoly(xy([16, 3.6, 29.4, 27, 2.6, 27]), 3)),
    flat(INK, softPoly(xy([14.4, 10.6, 17.6, 10.6, 16.9, 19.4, 15.1, 19.4]), [1.2, 1.2, 0.8, 0.8])),
    flat(INK, circle(16, 23, 1.8)),
  ],
  /** A red question mark. */
  question: [
    fill(P.red, band([10.6, 10.4], -80, [[225, 5.4], [-55, 3.6], 1.6], 5.4)),
    fill(P.red, circle(16.4, 26.2, 2.7)),
  ],
  /** A red "100", underlined twice. */
  hundred: [
    fill(P.red, bar([8.2, 5], [6.4, 19], 1.8), { w: 1.2 }),
    fill(P.red, oval([14.4, 12], 4.6, 7.4, 10) + oval([14.4, 12], 1.8, 4.4, 10, true), { w: 1.2 }),
    fill(P.red, oval([23.4, 12], 4.6, 7.4, 10) + oval([23.4, 12], 1.8, 4.4, 10, true), { w: 1.2 }),
    fill(P.red, bar([4.8, 23], [27.2, 22], 1.6), { w: 1.2 }),
    fill(P.red, bar([6.8, 27.2], [25.4, 26.4], 1.6), { w: 1.2 }),
  ],
  /** A big round red push button on a gray base. */
  'red-button': [
    fill(P.charcoal, 'M3 17.8V20.8A13 5.4 0 0 0 29 20.8V17.8A13 5.4 0 0 0 3 17.8Z'),
    fill(P.gray, ellipse(16, 17.8, 13, 5.4)),
    fill(P.darkRed, 'M6.6 9.8V15.8A9.4 4 0 0 0 25.4 15.8V9.8A9.4 4 0 0 0 6.6 9.8Z'),
    fill(P.red, ellipse(16, 9.8, 9.4, 4)),
    shine(ellipse(12.4, 8.6, 2.6, 1.2), 0.6),
  ],
  /** A solid green dot (a progress pip, done). */
  'pip-on': [fill(P.green, circle(16, 16, 11)), shine(ellipse(12.2, 12, 3, 2), 0.5)],
  /** A white dot with an outline (a progress pip to go; the same size as pip-on). */
  'pip-off': [fill(P.white, circle(16, 16, 11))],
  /** A white play triangle in a green rounded square. */
  play: [fill(P.green, BOX), flat(P.white, softPoly(xy([12.2, 9.4, 23.6, 16, 12.2, 22.6]), 1.6))],
  /** A bold gold arrow pointing up (the HUD's objective arrow, turned toward the goal). */
  'arrow-up': arrow(0),
  /** The bold arrow, pointing down. */
  'arrow-down': arrow(180),
  /** The bold arrow, pointing left. */
  'arrow-left': arrow(-90),
  /** The bold arrow, pointing right. */
  'arrow-right': arrow(90),
  /** A gold arrow going right, then curving up (jump / hop off). */
  hop: [fill(P.gold, band([6.9, 22.5], 0, [6, [-90, 8.8]], 6.4, [8, 7]))],
  /** A gold arrow making a U-turn back to the left (return). */
  back: [fill(P.gold, band([11.54, 24.1], 0, [5.6, [-180, 6.4], 4.4], 6.4, [8, 7]))],
  /** Two gold arrows chasing each other around a loop (try again). */
  repeat: [
    fill(P.gold, band(add([16, 16], dir(196), 8), 286, [[112, 8]], 5.8, [6.6, 6])),
    fill(P.gold, band(add([16, 16], dir(16), 8), 106, [[112, 8]], 5.8, [6.6, 6])),
  ],
} satisfies Record<string, IconDef>;
